'use client';

import {
  ChatProps,
  useMaybeLayoutContext,
  ChatToggle,
  ChatCloseIcon,
  ChatEntry,
  useDataChannel,
  ReceivedChatMessage,
  useRoomContext,
  useLocalParticipant,
  useRemoteParticipants,
} from '@livekit/components-react';
import type { ChatMessage, ReceivedDataMessage } from '@livekit/components-core';
import React from 'react';
import clsx from 'clsx';
import { parseMessage } from '@/lib/message';

type ChatData = {
  id: number;
  content: string;
  content_type: string;
  type: string;
  sender: string;
  recipient?: string | null;
  timestamp: number;
};

type TokenMetadata = {
  user_type?: string;
  is_moderator?: boolean;
  admitted?: boolean;
};

const USER_TYPE_TEACHER_INT = 2;

export function CustomChat({ messageFormatter, ...props }: ChatProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const ulRef = React.useRef<HTMLUListElement>(null);
  const room = useRoomContext();
  const localParticipant = useLocalParticipant();
  const [isSending, setIsSending] = React.useState(false);
  const remoteParticipants = useRemoteParticipants();

  const [chatMessages, setChatMessages] = React.useState<ReceivedChatMessage[]>([]);

  const onMessage = React.useCallback(
    (msg: ReceivedDataMessage) => {
      const data = parseMessage<ChatData>(msg.payload);
      if (data.content_type !== 'text') return;
      const from = remoteParticipants.find((p) => p.identity === data.sender);
      setChatMessages((existingMessages) => {
        existingMessages.push({
          id: String(data.id),
          timestamp: data.timestamp * 1000,
          message: data.content,
          from,
        });
        return [...existingMessages];
      });
    },
    [remoteParticipants],
  );

  useDataChannel('chat', onMessage);

  const layoutContext = useMaybeLayoutContext();
  const lastReadMsgAt = React.useRef<ChatMessage['timestamp']>(0);

  const encoder = React.useMemo(() => new TextEncoder(), []);
  const recipients = React.useMemo(() => {
    const teachers: string[] = [];
    remoteParticipants.forEach((p) => {
      if (!p.metadata) return;
      const metadata = JSON.parse(p.metadata) as TokenMetadata;
      if (`${metadata.user_type}` === `${USER_TYPE_TEACHER_INT}`) {
        teachers.push(p.identity);
      }
    });
    return [room.localParticipant.identity, ...teachers];
  }, [room, remoteParticipants]);

  const send = React.useCallback(
    async (message: string) => {
      const msg = {
        id: crypto.randomUUID(),
        content: message,
        timestamp: Date.now() / 1000,
        sender: room.localParticipant.identity,
        content_type: 'text',
        type: 'STUDENT_TO_ADMIN',
      } as const;
      const jsonMsg = JSON.stringify(msg);
      const data = encoder.encode(jsonMsg);
      await room.localParticipant.publishData(data, {
        topic: 'chat',
        reliable: true,
        destinationIdentities: recipients,
      });
      return Promise.resolve(msg);
    },
    [recipients, room, encoder],
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (inputRef.current && inputRef.current.value.trim() !== '') {
      if (send) {
        setIsSending(true);
        const msg = await send(inputRef.current.value);
        setChatMessages((existingMessages) => {
          existingMessages.push({
            id: msg.id,
            timestamp: msg.timestamp * 1000,
            message: msg.content,
            from: localParticipant.localParticipant,
          });
          return [...existingMessages];
        });
        inputRef.current.value = '';
        inputRef.current.focus();
        setIsSending(false);
      }
    }
  }

  React.useEffect(() => {
    if (ulRef) {
      ulRef.current?.scrollTo({ top: ulRef.current.scrollHeight });
    }
  }, [ulRef, chatMessages]);

  React.useEffect(() => {
    if (!layoutContext || chatMessages.length === 0) {
      return;
    }

    if (
      layoutContext.widget.state?.showChat &&
      chatMessages.length > 0 &&
      lastReadMsgAt.current !== chatMessages[chatMessages.length - 1]?.timestamp
    ) {
      lastReadMsgAt.current = chatMessages[chatMessages.length - 1]?.timestamp;
      return;
    }

    const unreadMessageCount = chatMessages.filter(
      (msg) => !lastReadMsgAt.current || msg.timestamp > lastReadMsgAt.current,
    ).length;

    const { widget } = layoutContext;
    if (unreadMessageCount > 0 && widget.state?.unreadMessages !== unreadMessageCount) {
      widget.dispatch?.({ msg: 'unread_msg', count: unreadMessageCount });
    }
  }, [chatMessages, layoutContext]);

  return (
    <div {...props} className="lk-chat">
      <div className="lk-chat-header">
        Messages
        <ChatToggle className="lk-close-button">
          <ChatCloseIcon />
        </ChatToggle>
      </div>

      <ul className="lk-list lk-chat-messages" ref={ulRef}>
        {props.children
          ? chatMessages.map((msg, idx) =>
              cloneSingleChild(props.children, {
                entry: msg,
                key: msg.id ?? idx,
                messageFormatter,
              }),
            )
          : chatMessages.map((msg, idx, allMsg) => {
              const hideName = idx >= 1 && allMsg[idx - 1].from === msg.from;
              // If the time delta between two messages is bigger than 60s show timestamp.
              const hideTimestamp = idx >= 1 && msg.timestamp - allMsg[idx - 1].timestamp < 60_000;

              return (
                <ChatEntry
                  key={msg.id ?? idx}
                  hideName={hideName}
                  hideTimestamp={hideName === false ? false : hideTimestamp} // If we show the name always show the timestamp as well.
                  entry={msg}
                  messageFormatter={messageFormatter}
                />
              );
            })}
      </ul>
      <form className="lk-chat-form" onSubmit={handleSubmit}>
        <input
          className="lk-form-control lk-chat-form-input"
          disabled={isSending}
          ref={inputRef}
          type="text"
          placeholder="Enter a message..."
          onInput={(ev) => ev.stopPropagation()}
          onKeyDown={(ev) => ev.stopPropagation()}
          onKeyUp={(ev) => ev.stopPropagation()}
        />
        <button type="submit" className="lk-button lk-chat-form-button" disabled={isSending}>
          Send
        </button>
      </form>
    </div>
  );
}

function cloneSingleChild(
  children: React.ReactNode | React.ReactNode[],
  props?: Record<string, any>,
  key?: any,
) {
  return React.Children.map(children, (child) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child) && React.Children.only(children)) {
      if (child.props.class) {
        // make sure we retain classnames of both passed props and child
        props ??= {};
        props.class = clsx(child.props.class, props.class);
        props.style = { ...child.props.style, ...props.style };
      }
      return React.cloneElement(child, { ...props, key });
    }
    return child;
  });
}
